import { createContext, useContext, ReactNode } from "react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { getMe } from "../api/user/user";
import { useAuth } from "../auth/authProvider";

// Mirrors the backend's AuthorizationPolicies.Analyst policy (RequireRole(Analyst, SportingDirector, Admin)) -
// use this wherever a frontend gate should match "at least Analyst-tier" access, not just the literal role name.
export const ANALYST_TIER_ROLES = ["Analyst", "Sporting Director", "Admin"];

interface RolesContextType {
    roles: string[];
    hasRole: (...roleNames: string[]) => boolean;
    isLoading: boolean;
}

const RolesContext = createContext<RolesContextType | undefined>(undefined);

const meQueryOptions = queryOptions({
    queryKey: ["me"],
    queryFn: async () => (await getMe()).data,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
});

interface RolesProviderProps {
    children: ReactNode;
}

export const RolesProvider = ({ children }: RolesProviderProps) => {
    const { isAuthenticated } = useAuth();

    const { data, isLoading } = useQuery({
        ...meQueryOptions,
        enabled: isAuthenticated,
    });

    const roles = data?.roles ?? [];

    const hasRole = (...roleNames: string[]): boolean => roleNames.some((roleName) => roles.includes(roleName));

    return <RolesContext.Provider value={{ roles, hasRole, isLoading }}>{children}</RolesContext.Provider>;
};

export const useRoles = () => {
    const context = useContext(RolesContext);
    if (!context) {
        throw new Error("useRoles must be used within RolesProvider");
    }
    return context;
};
